/**
 * QuickInquiryModal — schlankes Anfrageformular für Kunden, die das große
 * Mietanfrage-Formular nicht ausfüllen möchten. Sendet an
 * POST /api/public/quick-inquiry und legt die Nachricht im Admin-Postfach ab.
 *
 * Verwendung:
 *   const [open, setOpen] = useState(false);
 *   <QuickInquiryModal open={open} onClose={() => setOpen(false)} />
 */
import React, { useState } from 'react';
import axios from 'axios';
import { X, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuickInquiryModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emailValid = /\S+@\S+\.\S+/.test(form.email);
  const canSend =
    form.name.trim().length > 0 &&
    emailValid &&
    form.phone.trim().length >= 3 &&
    form.message.trim().length >= 3;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend || busy) return;
    setBusy(true);
    setErr('');
    try {
      await axios.post(`${API}/public/quick-inquiry`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setSent(true);
    } catch (e2) {
      setErr('Konnte nicht gesendet werden. Bitte später erneut versuchen oder anrufen.');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    // Reset state on close so a re-open starts fresh.
    setForm({ name: '', email: '', phone: '', message: '' });
    setSent(false);
    setErr('');
    setBusy(false);
    onClose && onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in"
      onClick={handleClose}
      data-testid="quick-inquiry-modal"
    >
      <div
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 bg-gradient-to-r from-sky-600 to-sky-700 text-white" style={{ background: 'linear-gradient(90deg, #005898 0%, #0369a1 100%)' }}>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-sky-100 font-bold">
              Schnellanfrage
            </div>
            <div className="font-bold text-lg truncate">In 30 Sekunden anfragen</div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 inline-flex items-center justify-center"
            aria-label="Schließen"
            data-testid="quick-inquiry-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {sent ? (
          <div className="p-6 text-center space-y-3" data-testid="quick-inquiry-sent">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 inline-flex items-center justify-center text-3xl">
              ✓
            </div>
            <h3 className="text-xl font-bold text-neutral-900">Danke für deine Anfrage!</h3>
            <p className="text-sm text-neutral-600">
              Wir melden uns telefonisch oder per E-Mail bei <b>{form.email}</b> zurück – meist innerhalb von 24 Stunden.
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="mt-2 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold px-5 py-2.5 text-sm"
              style={{ background: '#005898' }}
              data-testid="quick-inquiry-close-done"
            >
              Alles klar!
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <p className="text-xs text-neutral-500 leading-relaxed">
              Trag einfach Deine Kontaktdaten ein. Wir melden uns per E-Mail oder Telefon bei Dir und klären alles Weitere gemeinsam.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  required
                  className="w-full rounded-full border border-neutral-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  placeholder="Vor- & Nachname"
                  data-testid="quick-inquiry-name"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Telefon *</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  required
                  className="w-full rounded-full border border-neutral-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  placeholder="z.B. 0171 1234567"
                  data-testid="quick-inquiry-phone"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">E-Mail *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                required
                className="w-full rounded-full border border-neutral-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                placeholder="du@beispiel.de"
                data-testid="quick-inquiry-email"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Nachricht *</label>
              <textarea
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                rows={4}
                required
                className="w-full rounded-2xl border border-neutral-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                placeholder="Wonach suchst du? Wann und wo soll es stattfinden?"
                data-testid="quick-inquiry-message"
              />
            </div>

            {err && (
              <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSend || busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sky-700 hover:bg-sky-800 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white font-bold py-3 text-sm shadow transition-colors"
              style={{ background: (!canSend || busy) ? undefined : '#005898' }}
              data-testid="quick-inquiry-submit"
            >
              <Send className="h-4 w-4" />
              {busy ? 'Wird gesendet…' : 'Anfrage absenden'}
            </button>
            <p className="text-[11px] text-neutral-400 text-center leading-tight">
              Deine Angaben werden nur genutzt, um deine Anfrage zu beantworten. Keine Werbung.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default QuickInquiryModal;
